import React, { useCallback, useEffect, useRef, useState } from "react";
import type { FollowUpStatusItem } from "../../../types";
import { servicesApi } from "../../../services";
import { formatDateForDisplay } from "../../../utils/dateUtils";
import {
  getFollowUpWhatsAppMessage,
  getWhatsAppButtonStyles,
  getWhatsAppIconStyles,
  sendWhatsAppMessage,
  WHATSAPP_ICON_PATH,
} from "../../../utils/whatsappUtils";
import { ErrorState, Icons, Loading } from "../../shared";

export interface AppointmentFollowUpPanelProps {
  clientId: number;
  clientCarId?: number;
  clientName: string;
  clientPhone?: string | null;
  isCarCompany: boolean;
  onExpiredCountChange?: (count: number) => void;
  onScheduleAppointment: (params: {
    clientId: number;
    serviceId: number;
    clientCarId?: number;
    subServiceIds?: number[];
  }) => void;
}

export const AppointmentFollowUpPanel: React.FC<
  AppointmentFollowUpPanelProps
> = ({
  clientId,
  clientCarId,
  clientName,
  clientPhone,
  isCarCompany,
  onExpiredCountChange,
  onScheduleAppointment,
}) => {
  const [items, setItems] = useState<FollowUpStatusItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const onExpiredCountChangeRef = useRef(onExpiredCountChange);
  onExpiredCountChangeRef.current = onExpiredCountChange;

  const missingCar = isCarCompany && clientCarId == null;
  const subjectLabel = isCarCompany ? "veículo" : "cliente";

  const loadStatus = useCallback(async () => {
    if (missingCar) {
      setItems([]);
      setLoading(false);
      setError(null);
      onExpiredCountChangeRef.current?.(0);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await servicesApi.getFollowUpStatus({
        clientId,
        clientCarId,
      });

      if (res.success && res.data) {
        setItems(res.data.items);
        onExpiredCountChangeRef.current?.(
          res.data.items.filter((item) => item.isExpired).length,
        );
      } else {
        setItems([]);
        onExpiredCountChangeRef.current?.(0);
        setError(res.error || "Erro ao carregar validade dos serviços");
      }
    } catch (e) {
      setItems([]);
      onExpiredCountChangeRef.current?.(0);
      setError(
        e instanceof Error
          ? e.message
          : "Erro ao carregar validade dos serviços",
      );
    } finally {
      setLoading(false);
    }
  }, [clientId, clientCarId, missingCar]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  if (missingCar) {
    return (
      <div className="flex flex-col items-center text-center py-10 px-4">
        <Icons.Car sx={{ fontSize: 48 }} className="text-gray-300" />
        <p className="mt-3 text-sm text-gray-600">
          Vincule um veículo a este agendamento para ver o histórico de
          validade dos serviços.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <Loading
        message="Carregando validade dos serviços…"
        size="md"
        className="py-10"
      />
    );
  }

  if (error) {
    return (
      <ErrorState
        message={error}
        variant="inline"
        onRetry={loadStatus}
        className="py-6"
      />
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center text-center py-10 px-4">
        <Icons.Check sx={{ fontSize: 48 }} className="text-emerald-400" />
        <h4 className="mt-3 text-base font-semibold text-gray-900">
          Nenhum serviço com validade
        </h4>
        <p className="mt-1 text-sm text-gray-600 max-w-sm">
          Este {subjectLabel} ainda não possui atendimentos de serviços com
          prazo de validade cadastrado.
        </p>
      </div>
    );
  }

  const hasPhone = Boolean(clientPhone?.trim());

  return (
    <ul className="divide-y divide-gray-100 rounded-xl border border-gray-100 overflow-hidden">
      {items.map((item) => {
        const daysSince = item.daysSinceLastAppointment ?? 0;
        const daysRemaining = item.expirationDays - daysSince;
        const scheduleServiceId = item.scheduleServiceId ?? item.id;

        return (
          <li key={`${item.kind}-${item.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-3 py-3 sm:px-4 hover:bg-sky-50/60 transition-colors">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-gray-900">{item.name}</p>
                  {item.isExpired ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                      Vencido
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                      Em dia
                    </span>
                  )}
                </div>
                {item.lastAppointmentDate ? (
                  <p className="text-xs text-gray-500 mt-1">
                    Último atendimento:{" "}
                    <span className="font-medium text-gray-700">
                      {formatDateForDisplay(item.lastAppointmentDate)}
                    </span>
                    {" · "}
                    {item.isExpired ? (
                      <span className="font-semibold text-amber-700">
                        {daysSince} dias atrás
                      </span>
                    ) : (
                      <span className="font-semibold text-emerald-700">
                        restam {daysRemaining}{" "}
                        {daysRemaining === 1 ? "dia" : "dias"}
                      </span>
                    )}
                  </p>
                ) : null}
                <p className="text-xs text-gray-400 mt-0.5">
                  Validade: {item.expirationDays} dias
                </p>
              </div>

              {item.isExpired ? (
                <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                  {hasPhone ? (
                    <button
                      type="button"
                      onClick={() =>
                        sendWhatsAppMessage({
                          phoneNumber: clientPhone,
                          message: getFollowUpWhatsAppMessage(
                            clientName,
                            item.name,
                            daysSince,
                            item.expirationDays,
                          ),
                        })
                      }
                      className={getWhatsAppButtonStyles("sm")}
                      title="Enviar mensagem no WhatsApp"
                      aria-label={`Enviar mensagem de retorno por WhatsApp para ${clientName}`}
                    >
                      <svg
                        className={getWhatsAppIconStyles("sm")}
                        fill="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden
                      >
                        <path d={WHATSAPP_ICON_PATH} />
                      </svg>
                    </button>
                  ) : null}

                  <button
                    type="button"
                    onClick={() =>
                      onScheduleAppointment({
                        clientId,
                        serviceId: scheduleServiceId,
                        clientCarId,
                        subServiceIds:
                          item.kind === "subService" ? [item.id] : undefined,
                      })
                    }
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-sky-500 hover:bg-sky-600 text-white text-xs sm:text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
                    title="Agendar atendimento"
                    aria-label={`Agendar ${item.name} para ${clientName}`}
                  >
                    <Icons.Event sx={{ fontSize: 16 }} />
                    <span>Agendar</span>
                  </button>
                </div>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
};
